/* eslint-disable @typescript-eslint/no-explicit-any */
import { isAuthenticated, setAccessToken } from '@partiu/api/axios';
import { IUser } from '@partiu/api/types';
import { useUpdateProfileMutation } from '@partiu/api/user/hooks';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from './ThemeProvider';
import ModalCompleteProfile from '../components/organisms/ModalCompleteProfile';
import { useDisclosure, useToast } from '@chakra-ui/react';

interface IProfileContext {
  profile: IUser | null;
  isLoading: boolean;
  updateProfile: (data: Record<string, string | boolean>) => void;
  updateProfileImage: (file: File) => void;
  logout: () => void;
}

const defaultProfileContextData: IProfileContext = {
  profile: null,
  updateProfile: () => {},
  updateProfileImage: () => {},
  logout: () => {},
  isLoading: false,
};

const ProfileContext = createContext(defaultProfileContextData);

export function ProfileProvider({ children }: PropsWithChildren) {
  const [data, setData] = useState(defaultProfileContextData);
  const { id } = useTheme();

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const onUpdateSuccess = useCallback(
    (profile: IUser | null) => {
      if (!profile) return;

      localStorage.setItem('profile', JSON.stringify(profile));
      setData((data) => ({ ...data, profile }));

      if (!profile.cpf) {
        onOpen();
      } else {
        onClose();
      }
    },
    [data]
  );
  const updateProfileMutation = useUpdateProfileMutation({
    onSuccess: onUpdateSuccess,
    onError: (error: any) => {
      const errorMessage =
        Object.values(error.response?.data || {})
          .flat()
          .join(', ') || 'An error occurred';

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const isLoading = useMemo(() => updateProfileMutation.isLoading, [updateProfileMutation]);
  const updateProfile = useCallback(
    (data: Record<string, string | boolean>) => {
      setData((data) => ({
        ...data,
      }));
      if (!id) return;
      updateProfileMutation.mutate({ ...data, agreementId: id.toString() });
    },
    [updateProfileMutation]
  );

  const updateProfileImage = useCallback(
    (file: File) => {
      const dataform: any = new FormData();
      dataform.append('image', file);
      updateProfileMutation.mutate(dataform);
    },
    [updateProfileMutation]
  );

  useEffect(() => {
    if (!isAuthenticated()) return;
    const savedProfile = JSON.parse(localStorage.getItem('profile') || 'null');
    if (savedProfile) setData((data) => ({ ...data, profile: savedProfile }));

    if (id) updateProfileMutation.mutate({ agreementId: id.toString() });
  }, [id]);

  return (
    <ProfileContext.Provider
      value={{
        ...data,
        isLoading: isLoading,
        updateProfile,
        updateProfileImage,
        logout() {
          const existingUrlData = localStorage.getItem('urlData');

          setAccessToken(null);
          localStorage.clear();

          if (existingUrlData) localStorage.setItem('urlData', existingUrlData);

          window.location.reload();
        },
      }}
    >
      <ModalCompleteProfile updateProfile={updateProfile} profileData={data} isOpen={isOpen} onClose={onClose} />
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
